import React, { useState, useEffect, useMemo } from 'react';
import apiClient from '../../services/api';

interface User {
  id: string;
  name: string;
  role: { name: string };
  userSkills?: { skill: { name: string } }[];
  isActive?: boolean;
  isAgency?: boolean;
}

interface LeaveConflict {
  userId: string;
  startDate: string;
  endDate: string;
  reason: string;
}

export interface StaffSelectionResult {
  excludedUserIds: string[];
  weekendOfficeUserIds: string[];
}

interface StaffSelectionStepProps {
  users: User[];
  startDate: string;
  endDate: string;
  onBack: () => void;
  onNext: (selection: StaffSelectionResult) => void;
  loading?: boolean;
}

type StaffGroup = 'controllers' | 'lhr' | 'lgw' | 'office' | 'warehouse';

const GROUP_CONFIG: Record<StaffGroup, { label: string; color: string; border: string; bg: string }> = {
  controllers: { label: 'Controllers', color: 'text-orange-800', border: 'border-orange-200', bg: 'bg-orange-50' },
  lhr: { label: 'LHR Clearers', color: 'text-blue-800', border: 'border-blue-200', bg: 'bg-blue-50' },
  lgw: { label: 'LGW Clearers', color: 'text-emerald-800', border: 'border-emerald-200', bg: 'bg-emerald-50' },
  office: { label: 'Office Staff', color: 'text-gray-800', border: 'border-gray-200', bg: 'bg-gray-50' },
  warehouse: { label: 'Warehouse', color: 'text-amber-800', border: 'border-amber-200', bg: 'bg-amber-50' },
};

const hasSkill = (user: User, ...keywords: string[]): boolean => {
  if (!user.userSkills?.length) return false;
  return keywords.some(kw =>
    user.userSkills!.some(us => us.skill.name.toLowerCase().includes(kw.toLowerCase()))
  );
};

const StaffSelectionStep: React.FC<StaffSelectionStepProps> = ({
  users,
  startDate,
  endDate,
  onBack,
  onNext,
  loading = false,
}) => {
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set());
  const [weekendOfficeIds, setWeekendOfficeIds] = useState<Set<string>>(new Set());
  const [leaveConflicts, setLeaveConflicts] = useState<LeaveConflict[]>([]);

  useEffect(() => {
    loadLeaveConflicts();
  }, [startDate, endDate]);

  const loadLeaveConflicts = async () => {
    try {
      const res = await apiClient.get('/leave', {
        params: { status: 'APPROVED' }
      });
      const leaves = res.data?.data || res.data || [];
      const start = new Date(startDate);
      const end = new Date(endDate);

      const conflicts = leaves
        .filter((l: any) => {
          const lStart = new Date(l.startDate);
          const lEnd = new Date(l.endDate);
          return lStart <= end && lEnd >= start;
        })
        .map((l: any) => ({
          userId: l.userId,
          startDate: l.startDate,
          endDate: l.endDate,
          reason: l.reason || 'Leave',
        }));
      setLeaveConflicts(conflicts);
    } catch {
      // Silently fail - leave data is supplementary
    }
  };

  // Admin roles that should NOT appear in operational groups
  const isAdminRole = (roleName: string): boolean => {
    const upper = roleName.toUpperCase();
    return upper.includes('ADMIN') || upper.includes('SUPER') || upper.includes('MANAGER') || upper.includes('SYSTEM');
  };

  const groupedUsers = useMemo(() => {
    const groups: Record<StaffGroup, User[]> = {
      controllers: [],
      lhr: [],
      lgw: [],
      office: [],
      warehouse: [],
    };

    users.forEach(user => {
      const roleName = user.role?.name?.toUpperCase() || '';

      // Skip admins from operational groups entirely
      if (isAdminRole(roleName)) return;

      // CONTROLLER role → controllers group
      if (roleName.includes('CONTROLLER')) {
        groups.controllers.push(user);
        return;
      }

      // OFFICE role → office group
      if (roleName.includes('OFFICE')) {
        groups.office.push(user);
        return;
      }

      // WAREHOUSE role → warehouse group
      if (roleName.includes('WAREHOUSE')) {
        groups.warehouse.push(user);
        return;
      }

      // Clearers: classify by skills
      if (roleName.includes('CLEARER') || roleName.includes('CLEAR')) {
        const isLHR = hasSkill(user, 'lhr', 'heathrow');
        const isLGW = hasSkill(user, 'lgw', 'gatwick');

        if (isLHR) groups.lhr.push(user);
        if (isLGW) groups.lgw.push(user);
        if (!isLHR && !isLGW) groups.lhr.push(user); // Default to LHR
        return;
      }

      // Fallback: try to classify by skills (strictly match control training, not just 'control')
      if (hasSkill(user, 'lhr', 'heathrow')) groups.lhr.push(user);
      else if (hasSkill(user, 'lgw', 'gatwick')) groups.lgw.push(user);
      else if (hasSkill(user, 'control training', 'controlling')) groups.controllers.push(user);
      else if (hasSkill(user, 'office training')) groups.office.push(user);
      else if (hasSkill(user, 'warehouse')) groups.warehouse.push(user);
    });

    return groups;
  }, [users]);

  // Users eligible for weekend office — ALL users with 'office training' skill
  const weekendOfficeEligible = useMemo(() => {
    return users.filter(u => {
      const roleName = u.role?.name?.toUpperCase() || '';
      // Exclude admins
      if (isAdminRole(roleName)) return false;
      return hasSkill(u, 'office training');
    });
  }, [users]);

  const toggleUser = (userId: string) => {
    setExcludedIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const toggleWeekendOffice = (userId: string) => {
    setWeekendOfficeIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const selectAll = (group: StaffGroup) => {
    setExcludedIds(prev => {
      const next = new Set(prev);
      groupedUsers[group].forEach(u => next.delete(u.id));
      return next;
    });
  };

  const deselectAll = (group: StaffGroup) => {
    setExcludedIds(prev => {
      const next = new Set(prev);
      groupedUsers[group].forEach(u => next.add(u.id));
      return next;
    });
  };

  const getUserLeaveConflict = (userId: string): LeaveConflict | undefined => {
    return leaveConflicts.find(lc => lc.userId === userId);
  };

  const totalSelected = users.filter(u => !excludedIds.has(u.id)).length;

  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-semibold text-lg">Step 3: Staff Selection</h3>
        <p className="text-sm text-gray-600">
          Select which staff to include in the roster. Uncheck to exclude.
          <span className="ml-2 font-medium text-blue-700">{totalSelected} / {users.length} selected</span>
        </p>
      </div>

      {/* Staff Groups */}
      {(Object.keys(GROUP_CONFIG) as StaffGroup[]).map(group => {
        const cfg = GROUP_CONFIG[group];
        const groupUsers = groupedUsers[group];
        if (groupUsers.length === 0) return null;

        const selectedCount = groupUsers.filter(u => !excludedIds.has(u.id)).length;

        return (
          <div key={group} className={`rounded-lg border ${cfg.border} ${cfg.bg} overflow-hidden`}>
            <div className="px-4 py-3 flex items-center justify-between">
              <h4 className={`font-bold ${cfg.color} text-sm`}>
                {cfg.label}
                <span className="ml-2 font-normal text-gray-500">
                  ({selectedCount}/{groupUsers.length})
                </span>
              </h4>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => selectAll(group)}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Select All
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={() => deselectAll(group)}
                  className="text-xs text-red-600 hover:text-red-800 font-medium"
                >
                  Deselect All
                </button>
              </div>
            </div>

            <div className="px-4 pb-3 space-y-1">
              {groupUsers.map(user => {
                const isExcluded = excludedIds.has(user.id);
                const leave = getUserLeaveConflict(user.id);

                return (
                  <label
                    key={user.id}
                    className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors ${
                      isExcluded ? 'opacity-50 bg-white/50' : 'hover:bg-white/70'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={!isExcluded}
                      onChange={() => toggleUser(user.id)}
                      className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {user.name}
                        </span>
                        {user.isAgency && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded font-medium">
                            AGENCY
                          </span>
                        )}
                        {leave && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-medium">
                            ON LEAVE
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {user.userSkills?.map(us => (
                          <span
                            key={us.skill.name}
                            className="text-[10px] px-1.5 py-0.5 bg-white border border-gray-200 text-gray-600 rounded"
                          >
                            {us.skill.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Weekend Office Coverage */}
      {weekendOfficeEligible.length > 0 && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 overflow-hidden">
          <div className="px-4 py-3">
            <h4 className="font-bold text-indigo-800 text-sm">
              📅 Weekend Office Coverage
              <span className="ml-2 font-normal text-gray-500">
                ({weekendOfficeIds.size} selected)
              </span>
            </h4>
            <p className="text-xs text-indigo-600 mt-1">
              Select staff with office training to cover weekend office shifts
            </p>
          </div>
          <div className="px-4 pb-3 space-y-1">
            {weekendOfficeEligible.map(user => {
              const isSelected = weekendOfficeIds.has(user.id);
              return (
                <label
                  key={user.id}
                  className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors ${
                    isSelected ? 'bg-indigo-100/70' : 'hover:bg-white/70'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleWeekendOffice(user.id)}
                    className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm font-medium text-gray-900">{user.name}</span>
                  <span className="text-xs text-gray-500">({user.role?.name})</span>
                  <div className="flex gap-1">
                    {user.userSkills?.map(us => (
                      <span
                        key={us.skill.name}
                        className="text-[10px] px-1.5 py-0.5 bg-white border border-gray-200 text-gray-600 rounded"
                      >
                        {us.skill.name}
                      </span>
                    ))}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-4 border-t">
        <button
          onClick={onBack}
          className="px-4 py-2 border rounded hover:bg-gray-50"
        >
          ← Back
        </button>
        <button
          onClick={() => onNext({
            excludedUserIds: Array.from(excludedIds),
            weekendOfficeUserIds: Array.from(weekendOfficeIds),
          })}
          disabled={loading}
          className="px-6 py-2 bg-green-600 text-white rounded font-bold hover:bg-green-700 disabled:opacity-50"
        >
          {loading ? 'Generating...' : 'Generate Roster →'}
        </button>
      </div>
    </div>
  );
};

export default StaffSelectionStep;
